/**
 * tokenStore — single source of truth for the access token.
 *
 * Primary storage: memory (not accessible to XSS scripts).
 * Fallback:       localStorage for hydration on page reload.
 * Sync:           also mirrors to localStorage('token') so existing
 *                 localStorage.getItem('token') calls continue to work.
 *
 * The refresh token is NEVER stored here — it lives exclusively in the
 * httpOnly cookie that the backend sets, which is inaccessible to JS.
 */

let _accessToken = null;

export const tokenStore = {
  /** Return the current access token (hydrates from localStorage if needed). */
  get() {
    // If memory is empty, try to hydrate from localStorage
    if (!_accessToken) {
      try {
        const stored = localStorage.getItem('token');
        if (stored) {
          _accessToken = stored;
        }
      } catch (e) {
        // localStorage might be unavailable (private browsing, etc)
      }
    }
    return _accessToken;
  },

  /** Store a new access token (memory + localStorage). */
  set(token) {
    _accessToken = token;
    if (token) {
      try {
        localStorage.setItem('token', token);
      } catch (e) {
        console.warn('Failed to save token to localStorage:', e);
      }
    } else {
      try {
        localStorage.removeItem('token');
      } catch (e) {
        console.warn('Failed to remove token from localStorage:', e);
      }
    }
  },

  /** Clear the access token (called on logout or auth failure). */
  clear() {
    _accessToken = null;
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.warn('Failed to clear token from localStorage:', e);
    }
  },

  /** True when an access token is present. */
  isAuthenticated() {
    return Boolean(this.get()); // Use get() to hydrate if needed
  },

  /**
   * Refresh-token fallback (used when the httpOnly cookie isn't delivered —
   * e.g. Safari ITP, Brave, third-party-cookie blocking, or after the cookie
   * expires while the tab was backgrounded).
   *
   * Stored in localStorage so it survives tab close, new windows, and
   * browser restart. Reads also fall back to sessionStorage for one-time
   * migration of existing sessions.
   */
  getRefreshToken() {
    try {
      const v = localStorage.getItem('refresh_token');
      if (v) return v;
      // Migration: pull from old sessionStorage location and promote it
      const legacy = sessionStorage.getItem('refresh_token');
      if (legacy) {
        try { localStorage.setItem('refresh_token', legacy); } catch (_) {}
        try { sessionStorage.removeItem('refresh_token'); } catch (_) {}
        return legacy;
      }
    } catch (e) { /* private browsing etc */ }
    return null;
  },

  setRefreshToken(token) {
    try {
      if (token) localStorage.setItem('refresh_token', token);
      else localStorage.removeItem('refresh_token');
      // Always clear the legacy location so it can't go stale.
      sessionStorage.removeItem('refresh_token');
    } catch (e) { /* ignore */ }
  },

  clearRefreshToken() {
    try { localStorage.removeItem('refresh_token'); } catch (_) {}
    try { sessionStorage.removeItem('refresh_token'); } catch (_) {}
  },
};

export default tokenStore;
